/**
 * Share & Buy Unified Flow for Workspace
 * Merges Share + Buy Now into a single button flow
 *
 * Flow: Click -> saveDesign -> open share panel + create Shopify product concurrently
 *
 * Ported from designer — uses workspace store, config, logger
 */

import {
  saveDesignToSDS,
  createShopifyProductAsync,
  getPublishIntentStatus,
} from '../shopify/index.js'
import { createDesignShortLink, patchShortLinkTarget } from './short-links.js'
import {
  openSharePanel,
  updateBuyNowReady,
  updateBuyNowError,
  setRetryFn,
  closeSharePanel,
} from './panel.js'
import { showToast } from '../../../utils/toast.js'
import { createLogger } from '../../../utils/logger.js'
import { store } from '../../../core/store.js'

const log = createLogger('share-buy')

let isProcessing = false

/**
 * Unified Share & Buy click handler
 */
export async function handleShareBuyClick(event) {
  if (event) event.preventDefault()
  if (isProcessing) return

  // Check auth — open the login modal, not just a toast (journey: anonymous
  // users design freely; commerce is the login gate).
  const token = localStorage.getItem('mms_customer_token')
  if (!token) {
    showToast('Please sign in to buy', 'warning')
    store.setState('auth.showLoginModal', true)
    return
  }

  // Check SDK ready
  const isSDKReady = store.getState('design.isSDKReady')
  if (!isSDKReady && !window.SDSManager?.state?.isReady) {
    showToast('Design tool is loading, please wait...', 'info')
    return
  }

  isProcessing = true
  const btn = document.getElementById('btnShareBuy')

  try {
    // 1. Set button to saving state
    if (btn) {
      btn.disabled = true
      btn.innerHTML =
        '<div class="spinner" style="width:16px;height:16px"></div><span>Saving...</span>'
    }

    // 2. Save design via SDS SDK
    log.info('Share & Buy: saving design...')
    const endProductData = await saveDesignToSDS()

    if (!endProductData || !endProductData.productId) {
      throw new Error('Failed to save design — no product data returned')
    }

    log.info('Share & Buy: design saved, productId:', endProductData.productId)

    // (design-saved DOM event removed 2026-11-02 — the Credits listener it
    // existed for was retired with H-079; zero listeners remained.)

    // 4. Get username for short link
    const username = getUsername()

    // 5. Create short link (fast, KV write)
    let shortLinkResult = null
    try {
      shortLinkResult = await createDesignShortLink(endProductData, username)
      log.info('Share & Buy: short link created:', shortLinkResult?.short_url)
    } catch (e) {
      log.warn('Share & Buy: short link creation failed, continuing without it')
    }

    // 6. Open buy panel immediately
    openSharePanel(endProductData)

    // 7. Create Shopify product in background (fire-and-forget)
    const shopifyRetry = () => createShopifyAndUpdate(endProductData, shortLinkResult?.code)
    setRetryFn(shopifyRetry)
    shopifyRetry()
  } catch (error) {
    log.error('Share & Buy error:', error)
    showToast('Failed to save design: ' + error.message, 'error', 5000)
  } finally {
    // Restore button
    if (btn) {
      btn.disabled = false
      btn.innerHTML = '<i class="ph ph-shopping-bag-open"></i><span>Buy</span>'
    }
    isProcessing = false
  }
}

/**
 * Create Shopify product and update short link target
 */
async function createShopifyAndUpdate(endProductData, shortLinkCode) {
  try {
    log.info('Share & Buy: creating Shopify product...')
    const intent = await createShopifyProductAsync(endProductData)
    const shopifyProduct = await waitForPublishedIntent(intent.intent_id)

    if (shopifyProduct) {
      log.info('Share & Buy: Shopify product ready:', shopifyProduct.shopify_handle)
      updateBuyNowReady({
        ...shopifyProduct,
        price_usd_cents: endProductData.rawData?.price_usd_cents,
      })

      // Update short link target to Shopify URL
      if (shortLinkCode) {
        const shopifyUrl =
          shopifyProduct.shopify_product_url ||
          `https://mamamiya.store/products/${shopifyProduct.shopify_handle}`
        patchShortLinkTarget(shortLinkCode, shopifyUrl).catch((e) =>
          log.warn('Short link PATCH failed:', e)
        )
      }
    } else {
      updateBuyNowError(new Error('No product data returned'))
    }
  } catch (error) {
    log.error('Shopify product creation failed:', error)
    updateBuyNowError(error)
  }
}

function getUsername() {
  try {
    const customer = JSON.parse(localStorage.getItem('mms_customer') || '{}')
    return customer.username || customer.email?.split('@')[0] || 'user'
  } catch {
    return 'user'
  }
}

// Expose globally for onclick handler in HTML
window.handleShareBuyClick = handleShareBuyClick

export default { handleShareBuyClick }

async function waitForPublishedIntent(intentId) {
  for (let attempt = 0; attempt < 30; attempt += 1) {
    const status = await getPublishIntentStatus(intentId)
    if (status.status === 'published') return status.result
    if (status.status === 'failed') {
      throw new Error(status.error_message || 'Server-side product publishing failed')
    }
    await new Promise((resolve) => setTimeout(resolve, 1000))
  }
  throw new Error('Product publishing is still processing; please retry shortly')
}
